<template>
  <div class="coupon-container send-page">
    <el-card shadow="never">
      <el-steps :active="activeStep" align-center finish-status="success" class="send-steps">
        <el-step title="选择会员" description="按条件筛选或手动勾选" />
        <el-step title="选择券模板并预览" description="确认发放人数与张数" />
        <el-step title="确认发放" description="执行并查看结果" />
      </el-steps>
    </el-card>

    <!-- 第 1 步：选择会员 -->
    <el-card v-show="activeStep === 0" class="step-card" shadow="never">
      <el-form :inline="true" :model="memberFilter" class="filter-form">
        <el-form-item label="关键字">
          <el-input v-model="memberFilter.keyword" placeholder="手机号 / 姓名" clearable style="width: 150px;" @keyup.enter.native="searchMembers" />
        </el-form-item>
        <el-form-item label="注册时间">
          <el-date-picker
            v-model="regRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
          />
        </el-form-item>
        <el-form-item label="累计消费">
          <el-input-number v-model="memberFilter.min_money" :min="0" :precision="2" :controls="false" placeholder="最低" style="width: 100px;" />
          <span style="margin: 0 4px;">-</span>
          <el-input-number v-model="memberFilter.max_money" :min="0" :precision="2" :controls="false" placeholder="最高" style="width: 100px;" />
        </el-form-item>
        <el-form-item label="会员等级">
          <el-input v-model="memberFilter.rank" placeholder="等级" clearable style="width: 100px;" />
        </el-form-item>
        <el-form-item label="会员分组">
          <el-input v-model="memberFilter.group" placeholder="分组" clearable style="width: 120px;" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="searchMembers">查询</el-button>
          <el-button @click="resetMemberFilter">重置</el-button>
        </el-form-item>
      </el-form>

      <el-table
        ref="memberTable"
        v-loading="memberLoading"
        :data="memberList"
        height="46vh"
        border
        stripe
        row-key="crm_id"
        style="width: 100%"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" reserve-selection width="50" align="center" />
        <el-table-column prop="crm_id" label="会员ID" width="100" align="center" />
        <el-table-column prop="mem_user" label="手机号" min-width="130" />
        <el-table-column prop="mem_name" label="姓名" min-width="100">
          <template slot-scope="scope">{{ scope.row.mem_name || '-' }}</template>
        </el-table-column>
        <el-table-column prop="mem_rank" label="等级" width="80" align="center" />
        <el-table-column prop="group" label="分组" width="100" align="center">
          <template slot-scope="scope">{{ scope.row.group || '-' }}</template>
        </el-table-column>
        <el-table-column prop="reg_time" label="注册时间" width="160" />
        <el-table-column prop="consume_money" label="累计消费(元)" width="120" align="center" />
        <el-table-column prop="usable_coupon" label="可用券数" width="90" align="center" />
      </el-table>

      <div class="pager-row">
        <span class="selected-tip">
          已选会员 <b>{{ selectedMembers.length }}</b> 人（跨页保留勾选）
        </span>
        <el-pagination
          @size-change="handleMemberSizeChange"
          @current-change="handleMemberPageChange"
          :current-page="memberPage"
          :page-sizes="[20, 50, 100, 200]"
          :page-size="memberPageSize"
          layout="total, sizes, prev, pager, next"
          :total="memberTotal"
          background
        />
      </div>

      <div class="step-actions">
        <el-button type="primary" :disabled="selectedMembers.length === 0" @click="goStep2">
          下一步（已选 {{ selectedMembers.length }} 人）
        </el-button>
      </div>
    </el-card>

    <!-- 第 2 步：选择券模板并预览 -->
    <el-card v-show="activeStep === 1" class="step-card" shadow="never">
      <div class="section-title">选择要发放的券模板（新人专享券不可手动下发，已自动排除）</div>
      <el-checkbox-group v-model="selectedTemplateIds" class="template-checks" v-loading="templateLoading">
        <el-checkbox
          v-for="t in templateOptions"
          :key="t.id"
          :label="Number(t.id)"
          :disabled="!t.can_send || Number(t.display) !== 1"
          border
          class="template-check"
        >
          <span class="tpl-name">{{ t.name }}</span>
          <el-tag size="mini" :type="Number(t.type) === 1 ? 'warning' : 'success'" style="margin: 0 6px;">{{ t.type_text }}</el-tag>
          <span class="tpl-amount">¥{{ t.amount }}</span>
          <span class="tpl-min">{{ Number(t.min_amount) > 0 ? '满' + t.min_amount + '可用' : '无门槛' }}</span>
          <span class="tpl-limit">限领 {{ Number(t.per_limit) === 0 ? '不限' : t.per_limit + '张' }}</span>
          <span class="tpl-left" v-if="Number(t.total_limit) > 0">剩余 {{ t.left_count }} 张</span>
          <span class="tpl-left" v-else>不限量</span>
        </el-checkbox>
      </el-checkbox-group>

      <div class="step-actions">
        <el-button @click="activeStep = 0">上一步</el-button>
        <el-button type="primary" :loading="previewLoading" :disabled="selectedTemplateIds.length === 0" @click="doPreview">
          生成发放预览
        </el-button>
        <el-button type="success" :disabled="!preview || !preview.ok" @click="goStep3">下一步</el-button>
      </div>

      <!-- 预览结果 -->
      <div v-if="preview" class="preview-box">
        <el-alert
          :title="preview.ok ? '预览通过，请确认后进入下一步' : (preview.msg || '本次选择的模板不可发放')"
          :type="preview.ok ? 'success' : 'error'"
          :closable="false"
          show-icon
          style="margin-bottom: 12px;"
        />
        <el-alert
          v-if="preview.need_async === 1"
          title="发放人数较多（超过 100 人），执行时间可能较长，请耐心等待"
          type="warning"
          :closable="false"
          show-icon
          style="margin-bottom: 12px;"
        />
        <el-row :gutter="16" class="preview-stats">
          <el-col :span="8"><div class="stat-item"><div class="num">{{ preview.member_total }}</div><div class="txt">发放人数</div></div></el-col>
          <el-col :span="8"><div class="stat-item"><div class="num">{{ preview.template_num }}</div><div class="txt">券模板数</div></div></el-col>
          <el-col :span="8"><div class="stat-item highlight"><div class="num">{{ preview.plan_total }}</div><div class="txt">预计发放张数</div></div></el-col>
        </el-row>
        <div v-if="preview.templates && preview.templates.length" class="preview-section">
          <div class="section-title small">券模板明细</div>
          <el-table :data="preview.templates" border size="small">
            <el-table-column prop="name" label="券名称" min-width="140" />
            <el-table-column prop="type_text" label="类型" width="90" align="center" />
            <el-table-column label="面额" width="90" align="center">
              <template slot-scope="scope">¥{{ scope.row.amount }}</template>
            </el-table-column>
            <el-table-column label="门槛" width="110" align="center">
              <template slot-scope="scope">{{ Number(scope.row.min_amount) > 0 ? '满' + scope.row.min_amount + '可用' : '无门槛' }}</template>
            </el-table-column>
            <el-table-column prop="per_limit" label="每人限领" width="90" align="center" />
            <el-table-column label="剩余/总量" width="100" align="center">
              <template slot-scope="scope">
                {{ Number(scope.row.total_limit) === 0 ? '不限量' : scope.row.left_count + ' / ' + scope.row.total_limit }}
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div v-if="preview.members && preview.members.length" class="preview-section">
          <div class="section-title small">会员预览（最多展示前 50 人）</div>
          <el-tag
            v-for="m in preview.members"
            :key="m.crm_id"
            size="medium"
            class="member-tag"
          >{{ m.mem_user }}{{ m.mem_name ? '(' + m.mem_name + ')' : '' }}</el-tag>
        </div>
        <div v-if="preview.warnings && preview.warnings.length" class="preview-section">
          <el-alert
            v-for="(w, i) in preview.warnings"
            :key="i"
            :title="w"
            type="warning"
            :closable="false"
            show-icon
            style="margin-bottom: 8px;"
          />
        </div>
      </div>
    </el-card>

    <!-- 第 3 步：确认发放 -->
    <el-card v-show="activeStep === 2" class="step-card" shadow="never">
      <div v-if="!sendResult">
        <el-row :gutter="16" class="preview-stats">
          <el-col :span="8"><div class="stat-item"><div class="num">{{ selectedMembers.length }}</div><div class="txt">发放人数</div></div></el-col>
          <el-col :span="8"><div class="stat-item"><div class="num">{{ selectedTemplateIds.length }}</div><div class="txt">券模板数</div></div></el-col>
          <el-col :span="8"><div class="stat-item highlight"><div class="num">{{ preview ? preview.plan_total : '-' }}</div><div class="txt">预计发放张数</div></div></el-col>
        </el-row>

        <el-form label-width="100px" style="margin-top: 20px;">
          <el-form-item label="发放备注">
            <el-input v-model="remark" type="textarea" :rows="3" maxlength="100" show-word-limit placeholder="选填，将记录到发券任务中便于审计" />
          </el-form-item>
        </el-form>

        <div class="step-actions">
          <el-button @click="activeStep = 1">上一步</el-button>
          <el-button type="danger" :loading="sending" @click="doSend">
            <i class="el-icon-promotion"></i> 确认发放
          </el-button>
        </div>
      </div>

      <!-- 发放结果 -->
      <div v-else class="result-box">
        <el-result
          :icon="sendResult.fail_num > 0 ? 'warning' : 'success'"
          :title="sendResult.msg || '发放完成'"
          :sub-title="'批次号：' + sendResult.batch_no"
        >
          <template slot="extra">
            <el-row :gutter="16" class="preview-stats">
              <el-col :span="8"><div class="stat-item"><div class="num">{{ sendResult.member_total }}</div><div class="txt">发放人数</div></div></el-col>
              <el-col :span="8"><div class="stat-item success"><div class="num">{{ sendResult.success_coupon }}</div><div class="txt">成功发放（张）</div></div></el-col>
              <el-col :span="8"><div class="stat-item" :class="{ danger: sendResult.fail_coupon > 0 }"><div class="num">{{ sendResult.fail_coupon }}</div><div class="txt">失败（张）</div></div></el-col>
            </el-row>
            <div v-if="sendResult.fail_reason && sendResult.fail_reason.length" class="fail-reasons">
              <div class="section-title small">失败原因汇总</div>
              <el-alert
                v-for="(r, i) in sendResult.fail_reason"
                :key="i"
                :title="r"
                type="error"
                :closable="false"
                show-icon
                style="margin-bottom: 8px;"
              />
            </div>
            <div v-if="sendResult.warnings && sendResult.warnings.length" class="fail-reasons">
              <el-alert
                v-for="(w, i) in sendResult.warnings"
                :key="i"
                :title="w"
                type="warning"
                :closable="false"
                show-icon
                style="margin-bottom: 8px;"
              />
            </div>
            <div class="step-actions">
              <el-button type="primary" @click="resetAll">再发一笔</el-button>
              <el-button @click="$router.push('/coupon/logs')">查看发券记录</el-button>
            </div>
          </template>
        </el-result>
      </div>
    </el-card>
  </div>
</template>

<script>
import { memberLists, templateLists, sendPreview, sendBatch } from '@/api/coupon'

export default {
  name: 'CouponSend',
  data () {
    return {
      activeStep: 0,
      // 第 1 步：会员
      memberFilter: {
        keyword: '',
        min_money: undefined,
        max_money: undefined,
        rank: '',
        group: ''
      },
      regRange: [],
      memberList: [],
      memberLoading: false,
      memberTotal: 0,
      memberPage: 1,
      memberPageSize: 20,
      selectedMembers: [],
      // 第 2 步：模板与预览
      templateOptions: [],
      templateLoading: false,
      selectedTemplateIds: [],
      previewLoading: false,
      preview: null,
      // 第 3 步：发放
      remark: '',
      sending: false,
      sendResult: null
    }
  },
  mounted () {
    this.fetchMembers()
    this.fetchTemplates()
    // 支持从券模板页「发券」按钮带入模板
    const tpl = this.$route.query.tpl
    if (tpl) {
      this.selectedTemplateIds = String(tpl).split(',').map(id => Number(id)).filter(Boolean)
    }
  },
  methods: {
    buildMemberParams () {
      const params = {
        page: this.memberPage,
        page_size: this.memberPageSize
      }
      if (this.memberFilter.keyword) params.keyword = this.memberFilter.keyword
      if (this.memberFilter.min_money !== undefined && this.memberFilter.min_money !== null && this.memberFilter.min_money !== '') params.min_money = this.memberFilter.min_money
      if (this.memberFilter.max_money !== undefined && this.memberFilter.max_money !== null && this.memberFilter.max_money !== '') params.max_money = this.memberFilter.max_money
      if (this.memberFilter.rank) params.rank = this.memberFilter.rank
      if (this.memberFilter.group) params.group = this.memberFilter.group
      if (this.regRange && this.regRange.length === 2) {
        params.start_time = this.regRange[0]
        params.end_time = this.regRange[1]
      }
      return params
    },

    async fetchMembers () {
      this.memberLoading = true
      try {
        const res = await memberLists(this.buildMemberParams())
        if (res && String(res.code) === '0') {
          this.memberList = res.data || []
          this.memberTotal = res.total || 0
        } else {
          this.memberList = []
          this.memberTotal = 0
          this.$message.error(res && res.msg ? res.msg : '获取会员列表失败')
        }
      } catch (error) {
        console.error('获取会员列表失败', error)
        this.memberList = []
        this.memberTotal = 0
        this.$message.error(error.message || '网络错误，请稍后重试')
      } finally {
        this.memberLoading = false
      }
    },

    searchMembers () {
      this.memberPage = 1
      this.clearSelection()
      this.fetchMembers()
    },

    resetMemberFilter () {
      this.memberFilter = { keyword: '', min_money: undefined, max_money: undefined, rank: '', group: '' }
      this.regRange = []
      this.memberPage = 1
      this.clearSelection()
      this.fetchMembers()
    },

    clearSelection () {
      // 清空跨页保留的勾选
      if (this.$refs.memberTable) {
        this.$refs.memberTable.clearSelection()
      }
      this.selectedMembers = []
    },

    handleMemberSizeChange (val) {
      this.memberPageSize = val
      this.memberPage = 1
      this.fetchMembers()
    },

    handleMemberPageChange (val) {
      this.memberPage = val
      this.fetchMembers()
    },

    handleSelectionChange (rows) {
      this.selectedMembers = rows
    },

    async fetchTemplates () {
      this.templateLoading = true
      try {
        const res = await templateLists({ page: 1, page_size: 200, display: 1 })
        if (res && String(res.code) === '0') {
          // 只展示可手动下发的模板（新人专享券 can_send=false）
          this.templateOptions = (res.data || []).filter(t => t.can_send)
        } else {
          this.templateOptions = []
        }
      } catch (error) {
        console.error('获取券模板失败', error)
        this.templateOptions = []
      } finally {
        this.templateLoading = false
      }
    },

    goStep2 () {
      if (this.selectedMembers.length === 0) {
        this.$message.warning('请先勾选要发放的会员')
        return
      }
      this.activeStep = 1
    },

    async doPreview () {
      if (this.selectedTemplateIds.length === 0) {
        this.$message.warning('请至少选择一个券模板')
        return
      }
      this.previewLoading = true
      this.preview = null
      try {
        const res = await sendPreview({
          crm_ids: this.selectedMembers.map(m => m.crm_id),
          template_ids: this.selectedTemplateIds
        })
        if (res && String(res.code) === '0') {
          this.preview = res.data
          if (!this.preview.ok) {
            this.$message.error(this.preview.msg || '所选模板均不可发放')
          }
        } else {
          this.$message.error(res && res.msg ? res.msg : '预览失败')
        }
      } catch (error) {
        console.error('发放预览失败', error)
        this.$message.error(error.message || '网络错误，请稍后重试')
      } finally {
        this.previewLoading = false
      }
    },

    goStep3 () {
      if (!this.preview || !this.preview.ok) {
        this.$message.warning('请先生成通过校验的发放预览')
        return
      }
      this.activeStep = 2
    },

    async doSend () {
      try {
        await this.$confirm(
          `确认向 ${this.selectedMembers.length} 名会员发放 ${this.selectedTemplateIds.length} 种券（预计 ${this.preview.plan_total} 张）吗？`,
          '发放确认',
          { confirmButtonText: '确认发放', cancelButtonText: '取消', type: 'warning' }
        )
      } catch (e) {
        return
      }
      this.sending = true
      try {
        // 会员筛选条件快照，原样存入任务表，便于审计复现
        const memberScope = JSON.stringify({
          manual_selected: true,
          filter: this.buildMemberParams()
        })
        const res = await sendBatch({
          crm_ids: this.selectedMembers.map(m => m.crm_id),
          template_ids: this.selectedTemplateIds,
          member_scope: memberScope,
          remark: this.remark
        })
        if (res && String(res.code) === '0') {
          this.sendResult = res.data
          if (this.sendResult.status === 3) {
            this.$message.error(this.sendResult.msg || '发放失败')
          } else if (this.sendResult.fail_num > 0) {
            this.$message.warning('发放完成，部分失败，请查看结果明细')
          } else {
            this.$message.success('发放完成')
          }
        } else {
          this.$message.error(res && res.msg ? res.msg : '发放失败')
        }
      } catch (error) {
        console.error('批量发券失败', error)
        this.$message.error(error.message || '网络错误，请稍后重试')
      } finally {
        this.sending = false
      }
    },

    resetAll () {
      this.activeStep = 0
      this.selectedTemplateIds = []
      this.preview = null
      this.remark = ''
      this.sendResult = null
      this.memberPage = 1
      this.clearSelection()
      this.fetchMembers()
    }
  }
}
</script>

<style lang="scss" scoped>
.coupon-container {
  padding: 20px;
  background-color: #f0f2f6;
  height: 85vh;
  box-sizing: border-box;
  overflow: auto;
}

.send-steps {
  padding: 10px 40px 0;
}

.step-card {
  margin-top: 16px;

  .filter-form {
    margin-bottom: 0;
  }
}

.pager-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;

  .selected-tip {
    color: #606266;

    b {
      color: #409eff;
      font-size: 16px;
    }
  }
}

.step-actions {
  margin-top: 20px;
  text-align: center;
}

.section-title {
  font-weight: 600;
  margin-bottom: 12px;
  color: #303133;

  &.small {
    font-size: 13px;
    margin: 14px 0 8px;
  }
}

.template-checks {
  display: flex;
  flex-direction: column;

  .template-check {
    margin: 0 0 10px 0;
    padding: 10px 14px;
    height: auto;
    max-width: 640px;

    .tpl-name {
      font-weight: 600;
    }

    .tpl-amount {
      color: #f56c6c;
      font-weight: 600;
      margin-right: 8px;
    }

    .tpl-min,
    .tpl-limit {
      color: #606266;
      margin-right: 8px;
      font-size: 13px;
    }

    .tpl-left {
      color: #e6a23c;
      font-size: 12px;
    }
  }
}

.preview-box {
  margin-top: 20px;
  padding: 16px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.preview-stats {
  text-align: center;

  .stat-item {
    padding: 16px 0;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 4px;

    .num {
      font-size: 26px;
      font-weight: 700;
      color: #409eff;
    }

    .txt {
      margin-top: 6px;
      color: #909399;
      font-size: 13px;
    }

    &.highlight .num {
      color: #f56c6c;
    }

    &.success .num {
      color: #67c23a;
    }

    &.danger .num {
      color: #f56c6c;
    }
  }
}

.preview-section {
  margin-top: 14px;
}

.member-tag {
  margin: 0 8px 8px 0;
}

.result-box {
  padding: 10px 20px;

  .fail-reasons {
    max-width: 500px;
    margin: 0 auto 10px;
    text-align: left;
  }
}
</style>
